// Dashboard.jsx
import {
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/clerk-react";
import { MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import BoardActions from "../components/molecules/BoardActions";
import KeyboardShortcutsHelp from "../components/molecules/KeyboardShortcutsHelp";
import MobileTipsTooltip from "../components/molecules/MobileTipsTooltip";
import KanbanBoard from "../components/organisms/KanbanBoard";
import BoardPageTemplate from "../components/templates/BoardPageTemplate";
import { useTaskContext } from "../providers/TaskProvider";
import { useKeyboardShortcuts } from "../utils/keyboardShortcuts";

// Define column structure with colors
const COLUMNS = [
  { id: "column-1", title: "Incoming", status: "Incoming", color: "#4db1e0" },
  { id: "column-2", title: "To Do", status: "Todo", color: "#3a9bc8" },
  {
    id: "column-3",
    title: "In Progress",
    status: "In Progress",
    color: "#6bc4f0",
  },
  { id: "column-4", title: "Ideation", status: "Ideation", color: "#7dd3fc" },
  { id: "column-5", title: "Done", status: "Done", color: "#3da0cf" },
  { id: "column-6", title: "Archived", status: "Archived", color: "#6b7280" },
];

export default function Dashboard() {
  const {
    tasks,
    loading: tasksLoading,
    updateTask,
    deleteTask,
  } = useTaskContext();
  const [activeTask, setActiveTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dialogRef = useRef(null);

  const { user } = useUser();
  const { organization } = useOrganization();
  const { organizationList } = useOrganizationList();

  // Check if the device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Register keyboard shortcuts
  useKeyboardShortcuts({
    a: () => setShowArchived(!showArchived),
    d: () => {
      if (taskCounts["Done"] > 0) {
        handleArchiveAllDone();
      }
    },
  });

  // Memoize the tasks count by status for performance
  const taskCounts = useMemo(() => {
    return COLUMNS.reduce((acc, column) => {
      acc[column.status] = tasks.filter(
        (t) => t.status === column.status
      ).length;
      return acc;
    }, {});
  }, [tasks]);

  // Configure DnD sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 2, // Easier activation
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 0, // No delay for touch devices
        tolerance: 5,
      },
    })
  );

  // Load organization users
  useEffect(() => {
    if (organization) {
      // Get users from organization
      const orgUsers = organization.memberships.map((membership) => ({
        id: membership.publicUserData.userId,
        firstName: membership.publicUserData.firstName,
        lastName: membership.publicUserData.lastName,
        imageUrl: membership.publicUserData.imageUrl,
      }));

      setUsers(orgUsers);
    }
  }, [organization]);

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    const activeTaskData = tasks.find((task) => task.id === active.id);

    if (activeTaskData) {
      setActiveTask(activeTaskData);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    // If no drop target or same as source, just cancel
    if (!over) {
      setActiveTask(null);
      return;
    }

    // Handle dropping tasks between columns or onto other tasks
    if (active.id !== over.id) {
      const isColumn = over.data?.current?.type === "column";
      const activeTaskId = active.id;

      // Default to not loading until we know we need to update
      let targetStatus = null;

      if (isColumn) {
        // Task was dropped into a column - get status from column data
        targetStatus = over.data.current.status;
      } else {
        // Task was dropped onto another task - find that task's status
        const overTask = tasks.find((task) => task.id === over.id);
        if (overTask) {
          targetStatus = overTask.status;
        }
      }

      // If we determined a target status, update the task
      if (targetStatus) {
        setLoading(true);
        try {
          // Update task status
          await updateTask(activeTaskId, {
            status: targetStatus,
          });
        } catch (error) {
          console.error("Error updating task status:", error);
        } finally {
          setLoading(false);
        }
      }
    }

    setActiveTask(null);
  };

  // Handle drag cancel
  const handleDragCancel = () => {
    setActiveTask(null);
  };

  // Handle archiving all done tasks
  const handleArchiveAllDone = async () => {
    if (dialogRef.current) {
      dialogRef.current.show();
    } else {
      confirmArchiveAllDone();
    }
  };

  // Confirm and execute archiving all done tasks
  const confirmArchiveAllDone = async () => {
    setIsArchiving(true);

    try {
      const doneTasks = tasks.filter((task) => task.status === "Done");

      // Update each task to Archived status
      for (const task of doneTasks) {
        await updateTask(task.id, { status: "Archived" });
      }

      console.log(`Archived ${doneTasks.length} tasks`);
    } catch (error) {
      console.error("Error archiving tasks:", error);
    } finally {
      setIsArchiving(false);

      if (dialogRef.current) {
        dialogRef.current.hide();
      }
    }
  };

  // Handle deleting all archived tasks
  const handleDeleteAllArchived = async () => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete all archived tasks? This cannot be undone.`
      )
    ) {
      setLoading(true);
      try {
        const archivedTasks = tasks.filter(
          (task) => task.status === "Archived"
        );

        // Delete each archived task
        for (const task of archivedTasks) {
          await deleteTask(task.id);
        }

        console.log(`Deleted ${archivedTasks.length} archived tasks`);
      } catch (error) {
        console.error("Error deleting archived tasks:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <BoardPageTemplate
        title="ELITA Automation Dashboard"
        subtitle="Drag and drop tasks between columns to update their status"
        headerActions={
          <BoardActions
            showArchived={showArchived}
            onToggleArchived={() => setShowArchived(!showArchived)}
            onArchiveAllDone={handleArchiveAllDone}
            onDeleteAllArchived={handleDeleteAllArchived}
            taskCounts={taskCounts}
            isArchiving={isArchiving}
          />
        }
        loading={tasksLoading || loading}
        totalTasks={tasks.length}
      >
        <KanbanBoard
          columns={COLUMNS}
          tasks={tasks}
          users={users}
          activeTask={activeTask}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          showArchived={showArchived}
          sensors={sensors}
        />
      </BoardPageTemplate>

      {isMobile && <MobileTipsTooltip />}
      <KeyboardShortcutsHelp />

      <sl-dialog ref={dialogRef} label="Archive All Done Tasks">
        <p>
          Are you sure you want to archive all {taskCounts["Done"]} tasks in the
          Done column?
        </p>
        <div slot="footer" className="flex justify-end space-x-2">
          <sl-button
            variant="neutral"
            onClick={() => dialogRef.current?.hide()}
          >
            Cancel
          </sl-button>
          <sl-button
            variant="primary"
            onClick={confirmArchiveAllDone}
            disabled={isArchiving}
          >
            {isArchiving ? "Archiving..." : "Archive All Done"}
          </sl-button>
        </div>
      </sl-dialog>
    </>
  );
}
